// Facebook Pixel helper — centralised tracking for the entire page
// Pixel ID: 1215304437119773
//
// Strategy: defer pixel loading until the user interacts or after a short idle
// period. This removes a large render-blocking 3rd-party script from the LCP path
// while still firing PageView on the same session.

const PIXEL_ID = '1215304437119773'
let pixelLoaded = false
let pixelLoadScheduled = false

/** Loads the actual Meta Pixel — only called once, after defer trigger fires */
function loadPixelNow() {
  if (typeof window === 'undefined') return
  if (pixelLoaded || (window as any).fbq) return
  pixelLoaded = true

  // 1. Create the fbq stub that queues calls until fbevents.js loads
  const f = window as any
  const n = (f.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
  })
  if (!f._fbq) f._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  // 2. Queue init + PageView — script will replay these when loaded
  ;(window as any).fbq('init', PIXEL_ID)
  ;(window as any).fbq('track', 'PageView')

  // 3. Load fbevents.js asynchronously
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
}

/**
 * Initialise the pixel — defers actual loading until first user interaction
 * (scroll/touch/click/keydown) OR after 3.5s idle, whichever comes first.
 * This keeps the LCP path clean while still tracking the visit.
 */
export function initFbPixel() {
  if (typeof window === 'undefined') return
  if (pixelLoadScheduled || pixelLoaded) return
  pixelLoadScheduled = true

  const triggerLoad = () => {
    cleanup()
    loadPixelNow()
  }

  const events: Array<keyof WindowEventMap> = [
    'scroll',
    'touchstart',
    'mousemove',
    'keydown',
    'click',
  ]

  const cleanup = () => {
    events.forEach((evt) =>
      window.removeEventListener(evt, triggerLoad as EventListener),
    )
  }

  events.forEach((evt) =>
    window.addEventListener(
      evt,
      triggerLoad as EventListener,
      {
        once: true,
        passive: true,
      } as AddEventListenerOptions,
    ),
  )

  // Fallback: load after 3.5s of idle so bots/no-interaction sessions are tracked
  const fallbackDelay = 3500
  if ('requestIdleCallback' in window) {
    ;(window as any).requestIdleCallback(
      () => {
        if (!pixelLoaded) triggerLoad()
      },
      { timeout: fallbackDelay },
    )
  } else {
    setTimeout(() => {
      if (!pixelLoaded) triggerLoad()
    }, fallbackDelay)
  }
}

/** Safe wrapper — fires only when fbq is loaded */
export function trackFbEvent(event: string, data?: Record<string, any>) {
  // If user clicks a CTA before defer fires, load immediately so we don't miss the event
  if (typeof window !== 'undefined' && !pixelLoaded) {
    loadPixelNow()
  }
  if (typeof window !== 'undefined' && (window as any).fbq) {
    ;(window as any).fbq('track', event, data)
  }
}

/** Custom events (use trackCustom instead of track) */
export function trackFbCustomEvent(event: string, data?: Record<string, any>) {
  if (typeof window !== 'undefined' && (window as any).fbq) {
    ;(window as any).fbq('trackCustom', event, data)
  }
}

/** Track when a section becomes visible (fires once per section) */
export function trackSectionViews(sectionMap: Record<string, string>) {
  if (typeof window === 'undefined') return () => {}

  const fired = new Set<string>()
  const observers: IntersectionObserver[] = []

  const timeoutId = setTimeout(() => {
    Object.entries(sectionMap).forEach(([selector, sectionName]) => {
      const el = document.querySelector(selector)
      if (!el) return

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !fired.has(sectionName)) {
            fired.add(sectionName)
            trackFbCustomEvent('SectionView', {
              section_name: sectionName,
              content_category: 'Kinesiología Carrera Resuelta',
            })
            observer.disconnect()
          }
        },
        { threshold: 0.3 },
      )
      observer.observe(el)
      observers.push(observer)
    })
  }, 500)

  return () => {
    clearTimeout(timeoutId)
    observers.forEach((o) => o.disconnect())
  }
}

/** Track scroll depth milestones (25%, 50%, 75%, 100%) */
export function trackScrollDepth() {
  if (typeof window === 'undefined') return () => {}

  const milestones = [25, 50, 75, 100]
  const fired = new Set<number>()
  let ticking = false

  const compute = () => {
    ticking = false
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    if (docHeight <= 0) return
    const percent = Math.round((scrollTop / docHeight) * 100)

    milestones.forEach((m) => {
      if (percent >= m && !fired.has(m)) {
        fired.add(m)
        trackFbCustomEvent('ScrollDepth', {
          depth_percent: m,
          content_category: 'Kinesiología Carrera Resuelta',
        })
      }
    })
  }

  const handler = () => {
    if (!ticking) {
      window.requestAnimationFrame(compute)
      ticking = true
    }
  }

  window.addEventListener('scroll', handler, { passive: true })
  return () => window.removeEventListener('scroll', handler)
}

/** Track time on page milestones (30s, 60s, 120s, 300s) */
export function trackTimeOnPage() {
  if (typeof window === 'undefined') return () => {}

  const milestones = [30, 60, 120, 300]
  const timers: ReturnType<typeof setTimeout>[] = []

  milestones.forEach((seconds) => {
    const timer = setTimeout(() => {
      trackFbCustomEvent('TimeOnPage', {
        seconds,
        content_category: 'Kinesiología Carrera Resuelta',
      })
    }, seconds * 1000)
    timers.push(timer)
  })

  return () => timers.forEach((t) => clearTimeout(t))
}
